/* eslint-disable */
/**
 * Created by liupeng on 2017/11/6.
 */
import _ from "lodash";
import qs from "qs";
import config, { composeServerCgi, composeServerHost } from "@/configs";
import CustomError from "@/utils/Error";
import * as Sentry from "@sentry/nextjs";
import { GetServerSidePropsContext } from "next/types";
import { IncomingHttpHeaders, ServerResponse } from "http";
import { parseServerCookie } from "./serverCookie";

const MOCK_URL = "https://yapi.yuewen.com/mock/2309";

const parseJSON = (response: any) => response.json();

const composeClientCgi = (url: string) => {
  if (config.envType === "local") {
    if (/^\/go/.test(url)) {
      return `${MOCK_URL}${url}`;
    }
  }
  return url;
};

type ServerFetchOptions = {
  url: string;
  data: any;
  options: any;
  ctx: GetServerSidePropsContext;
};

type ClientFetchOptions = {
  url: string;
  data: any;
  options: object;
};

export type FetchOptions = {
  url: string;
  data: Record<string, any>;
  options?: RequestInit;
  ctx?: GetServerSidePropsContext; // needed in server
};

export type FetchOptionsByForm = Omit<FetchOptions, "data"> & {
  data: BodyInit;
};

export type BaseRes<T> = {
  code?: number;
  msg?: string;
  data: T;
};

type RestResponse<T> = Promise<BaseRes<T>>;

type RestProps = {
  read: <T>(options: FetchOptions) => RestResponse<T>;
  create?: <T>(options: FetchOptions) => RestResponse<T>;
  form?: <T>(options: FetchOptionsByForm) => RestResponse<T>;
};
let REST: RestProps;

if (typeof window === "undefined") {
  const defaults = {
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "same-origin",
    // 设置10秒timeout
    timeout: 10000,
  };
  const checkCode = (url: string) => (json: any) => {
    if (json.code === 0) {
      // 上报正常状态
      return json;
    }
    const error = new CustomError(`REST checkCode error: ${json.code}`, {
      ...json,
      __url: url,
    });
    throw error;
  };
  REST = {
    read: ({ url, data, options = {}, ctx: _ctx }: FetchOptions) => {
      const ctx = _ctx as GetServerSidePropsContext;
      const headers = ctx.req.headers as IncomingHttpHeaders;
      const parsedCookie = ctx.req.cookies;
      const uuid = parsedCookie["webnovel_uuid"];
      const queries = {
        ...data,
        _csrfToken: parsedCookie._csrfToken,
      };

      const cookies = parseServerCookie({
        ...parsedCookie,
        appId: "1001",
        areaId: "4",
      });

      // 在本地调试时，用域名请求；
      // 非本地调试时，则用 IP 请求
      let path = composeServerCgi(url);
      let host = composeServerHost(url);
      const time = new Date().toString();
      const composeUrl = `${path}${url.includes("?") ? "&" : "?"}${qs.stringify(
        queries
      )}`;

      let op = _.merge({}, defaults, options, {
        headers: { ...headers, host, cookie: cookies },
        // handle redirect by manual
        redirect: "manual",
      }); // need cookie
      console.log(
        `UUID:${uuid} ${time} 收到 REST 请求，请求头是: ${JSON.stringify(
          op.headers
        )}`
      );
      console.log(
        `UUID:${uuid} ${time} 收到 REST 请求，请求地址是: ${composeUrl}`
      );
      return fetch(composeUrl, op)
        .then((response: Response) => {
          const { status } = response;
          const headers = response.headers as Headers;
          // 将 cookie 设置到 res 上面
          // 透传 set-cookie && server-timing

          // https://stackoverflow.com/questions/65729430/how-to-parse-multiple-set-cookies-using-node-js-solution
          // set-cookie 需要多次设置生效，不能合并，此处通过 raw 来获取原始数据
          // https://stackoverflow.com/questions/66287255/multiple-set-cookie-fields-in-nextjs-getserversideprops
          // set 时也 set 一个 array 实现多个 set-cookie，且不会被合并或遗漏

          // @ts-ignore
          const setCookieArr = headers.raw()["set-cookie"];
          if (setCookieArr) {
            ctx.res.setHeader("set-cookie", setCookieArr);
          }

          if (headers.get("server-timing")) {
            ctx.res.setHeader("server-timing", headers.get("server-timing")!);
          }

          return response;
        })
        .then(parseJSON)
        .then(checkCode(url))
        .catch((err) => {
          Sentry.captureException(err);
          console.log(`${composeUrl} 请求失败:`, err);
        });
    },
  };
}

if (typeof window !== "undefined") {
  const Cookie = require("js-cookie");
  const defaultData = {
    _csrfToken: Cookie.get("_csrfToken"),
  };
  const defaultOptions = () => ({
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "same-origin",
    // 设置10秒timeout
    timeout: 10000,
  });
  const checkStatus = (url: string) => (response: Response) => {
    if (response.status === 200) {
      // 上报正常状态
      return response;
    }
    if (response.status === 401) {
      // 登录
      return new Promise(() => {});
    }
    const error = new CustomError(
      `REST checkStatus error: ${response.status}`,
      response
    );
    // 上报请求错误至 YEP

    throw error;
  };

  const checkCode = (url: string) => (json: any) => {
    if (json.code === 0) {
      return json;
    }
    throw new CustomError(`REST checkStatus error: ${json.code}`, {
      ...json,
      __url: url,
    });
  };

  const fetchWithErrorHandle = (url: string, options: any) =>
    fetch(url, _.merge({}, defaultOptions(), options))
      .then(checkStatus(url))
      .then(parseJSON)
      .then(checkCode(url))
      .catch((err) => {
        Sentry.captureException(err);
        console.log(`${url} 请求失败:`, err);
      });

  REST = {
    /**
     *
     * @param {String} url 请求url
     * @param {Object} data 请求参数
     * @param {Boolean} isOffStandardApi 是否为非标准返回的请求(有code标识请求结果)
     */
    read({ url, data, options }: FetchOptions) {
      // URL 带有 query 时进行拼接
      const mergedQuery = _.merge({}, defaultData, data);

      const mergedUrl = `${composeClientCgi(url)}${
        url.includes("?") ? "&" : "?"
      }${qs.stringify(mergedQuery)}`;

      const mergedOptions = _.merge({ method: "GET" }, options);
      return fetchWithErrorHandle(mergedUrl, mergedOptions);
    },
    create({ url, data, options }: FetchOptions) {
      const stringifyData = qs.stringify(_.merge({}, defaultData, data));
      const mergedOptions = _.merge(
        {},
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        },
        { body: stringifyData },
        options
      );
      return fetchWithErrorHandle(`${composeClientCgi(url)}`, mergedOptions);
    },
    form({ url, data }: FetchOptionsByForm) {
      return fetch(url, {
        method: "POST",
        credentials: "same-origin",
        body: data,
      })
        .then(checkStatus(url))
        .then(parseJSON)
        .then(checkCode(url));
    },
  };
}
//@ts-ignore
export default REST;
